import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationUnread,
} from '../api/notificationsApi'
import { NOTIFICATIONS_POLL_MS, UNREAD_COUNT_KEY } from './useUnreadCount'
import type { Notification, NotificationQuery } from '../types'

/** Liste sorgularının ortak anahtar öneki — mutasyonlar bu önekle invalidate eder. */
export const NOTIFICATIONS_KEY = 'notifications'

/**
 * Bildirim listesi sunucu-durumu (058). Hem zil paneli (`size: 5`) hem Bildirimler sayfası bunu
 * kullanır — tek sorgu, tek uç (research.md D8).
 *
 * **Dil `queryKey`'in parçasıdır:** sunucu `subject`/`content` alanlarını `Accept-Language`'e göre
 * çözer (spec FR-012), dolayısıyla TR↔EN geçişinde yanıt **değişir** ve yeniden çekilmelidir.
 * (056'dan bilinçli fark: orada veri tek dilliydi ve dil anahtara girmiyordu.)
 *
 * **`keepPreviousData`:** sunucu-taraflı sayfalamada sayfa değişirken `isLoading` yeniden `true` olur;
 * paylaşılan `DataTable`'ın iskelet dalı paginator'ı render etmediği için sayfalama kontrolü kaybolup
 * tablo titrer. Önceki veri korunarak bu önlenir (repodaki diğer sunucu-sayfalı listelerle aynı kalıp).
 *
 * **Periyodik yenileme ({@link NOTIFICATIONS_POLL_MS}):** yeni bildirimin görünmesi için sayfa
 * yenilemek gerekmez. `keepPreviousData` sayesinde yenileme sırasında liste boşalmaz/titremez;
 * sekme arka plandayken React Query zamanlayıcıyı durdurur (`refetchIntervalInBackground` varsayılanı
 * `false`), yani açık ama bakılmayan sekmeler sunucuya yük bindirmez.
 *
 * @param options.poll Yoklama açık mı (varsayılan `true`). **Zil paneli `false` verir panel kapalıyken:**
 *   `NotificationsButton` Topbar'da kalıcı olarak mount'ludur, dolayısıyla yoklama açık kalsaydı
 *   kullanıcı paneli hiç açmasa bile bu liste (sayaçtan farklı olarak hedef çözümleme sorgularını da
 *   içerir) sürekli çalışırdı. Kapalı panelin tazelenmesinin görünür bir karşılığı da yoktur: panel her
 *   açılışta zaten `refetch` ediliyor. Rozetin canlılığı sayaç sorgusundan gelir.
 */
export function useNotifications(query: NotificationQuery, options?: { poll?: boolean }) {
  const { i18n } = useTranslation()

  return useQuery({
    queryKey: [
      NOTIFICATIONS_KEY,
      'list',
      i18n.language,
      query.page,
      query.size,
      query.status,
      query.from ?? null,
      query.to ?? null,
    ],
    queryFn: () => getNotifications(query),
    placeholderData: keepPreviousData,
    refetchInterval: options?.poll === false ? false : NOTIFICATIONS_POLL_MS,
  })
}

/**
 * Tek bildirimin okundu/okunmadı **anahtarı** (spec FR-017a) — zil paneli ve liste sayfası ortak
 * kullanır.
 *
 * <p>Yön parametreden gelir: `read: true` → okundu ucu, `false` → okunmadı ucu. İki uç da
 * idempotenttir ve gövde almaz, bu yüzden `409` beklenmez.
 *
 * <p>**İyimser güncelleme yoktur — kasıtlı:** anahtar tek tık, sunucu yanıtı da güncellenmiş satırın
 * kendisidir; `onSuccess`'te liste ve sayaç invalidate edilerek **gerçek durum** okunur. Hatada
 * (`onError` yolu) ekranda yanlış bir durum kalmaz, çünkü hiç değiştirilmemiştir; hata bildirimi
 * merkezî hata yönetimine (019) bırakılır.
 */
export function useToggleNotificationRead() {
  const qc = useQueryClient()

  return useMutation<Notification, unknown, { id: number; read: boolean }>({
    mutationFn: ({ id, read }) =>
      read ? markNotificationRead(id) : markNotificationUnread(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })
}

/**
 * "Tümünü Okundu Olarak İşaretle" mutasyonu (spec FR-033 / FR-036).
 *
 * Başarıda **hem** liste **hem** sayaç invalidate edilir. Hatada da (`onSettled`) aynısı yapılır:
 * spec FR-036 "liste/rozet **gerçek duruma göre** tazelenir" der — kısmi başarı ihtimalinde ekranda
 * yanlış bir görüntü bırakmamak için sunucudan yeniden okunur. Hata bildirimi merkezî hata
 * yönetimine (019) bırakılır.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })
}
